import { Link } from "@tanstack/react-router";
import { CircleSlash2, X } from "lucide-react";
import React, { useState } from "react";
import { BanquetSeatingIcon } from "~/icons/BanquetSeatingIcon";
import { BlockSeatingIcon } from "~/icons/BlockSeatingIcon";
import { ClassroomSeatingIcon } from "~/icons/ClassroomSeatingIcon";
import { PartyIcon } from "~/icons/PartyIcon";
import { StandingSeatingIcon } from "~/icons/StandingSeatingIcon";
import { TableSeatingIcon } from "~/icons/TableSeatingIcon";
import { TheatreSeatingIcon } from "~/icons/TheatreSeatingIcon";
import { UFormSeatingIcon } from "~/icons/UFormSeatingIcon";
import { XIcon } from "~/icons/XIcon";
import {
  groupImagesByCategory,
  HotelRoomWithImages,
  RoomWithImages,
} from "~/lib/server-functions/get-locations";
import noImageFound from "~/assets/no-image.svg";
import { Button } from "~/components/ui/button";
import ImageGalleryPopUp from "~/components/PopUp/ImageGalleryPopUp";
import { cn } from "~/lib/utils";
import { RichTextRenderer } from "~/components/RichTextRenderer";

const sections = [{ id: "images", label: "Bilder" }];

interface HotelRoomCardProps {
  hotelRoom: HotelRoomWithImages;
}

export function HotelRoomCard({ hotelRoom }: HotelRoomCardProps) {
  const [activeImageId, setActiveImageId] = useState<undefined | string>();
  const [showGallery, setShowGallery] = useState(false);

  const groupedRoomImages = groupImagesByCategory(hotelRoom?.images ?? []);

  const flatGroupImages = Object.values(groupedRoomImages).flatMap((g) => {
    return g.images.map((img) => ({
      ...img,
      path: img.path as string,
      categoryId: g.categoryId,
      category: g.name,
    }));
  });

  return (
    <div>
      <div
        key={hotelRoom.id}
        className="flex flex-col gap-4 py-6 last:pb-0 md:flex-row md:items-center"
      >
        <div className="flex gap-4 md:mr-[100px] shrink-0">
          {/* Image */}
          <button
            className={cn(
              "relative group",
              hotelRoom.images.length && "cursor-pointer"
            )}
            onClick={() => setShowGallery(true)}
          >
            <div className="relative">
              <img
                src={
                  (hotelRoom.images[0] && hotelRoom.images[0].path) ??
                  noImageFound
                }
                alt={hotelRoom.name?.german}
                className="h-[107px] w-[115px] shrink-0 rounded-lg object-cover"
              />
              {/* grey overlay */}
              <div className="absolute inset-0 bg-dove-gray/10 rounded-lg pointer-events-none" />
            </div>
            {hotelRoom.images.length ? (
              <div
                className="absolute right-2 bottom-2 z-20 px-2 py-0.5 rounded bg-white/80 font-medium"
                style={{ pointerEvents: "none" }}
              >
                {1}/{hotelRoom.images.length}
              </div>
            ) : null}
          </button>

          {/* Infos */}
          <div className="flex flex-col items-start">
            <div className="font-semibold">{hotelRoom.name?.german}</div>
            <RichTextRenderer richText={hotelRoom.description?.german ?? ""} />
          </div>
        </div>
      </div>

      {showGallery && (
        <ImageGalleryPopUp
          open={showGallery}
          onOpenChange={(val) => setShowGallery(val)}
          images={flatGroupImages}
          activeImageId={activeImageId}
          sections={[
            { id: -1, label: "Alle Bilder" },
            ...Object.values(groupedRoomImages)
              .filter((g) => g.categoryId !== null && g.categoryId !== -1)
              .map((g) => ({
                id: g.categoryId!,
                label: g.name!,
              })),
          ]}
        />
      )}
    </div>
  );
}
